// Coeficiente de arrastre axial. Ver docs/theory/drag.md.
import { layoutStack, noseRadius, referenceDiameter, totalLength } from '../rocket/geometry';
import type { Rocket, StackComponent } from '../rocket/types';
import { machOf, type FlightConditions } from './flight-conditions';

const RE_CRITICAL = 5e5;
const MIN_AIRSPEED = 1e-3;

/**
 * Coeficiente de fricción de placa plana (laminar o capa límite mixta), sin compresibilidad.
 * Por encima de Re_crit la fórmula mixta no puede quedar por debajo del valor laminar, que es su
 * cota inferior física; con ello la curva es continua en Re_crit y sube al empezar la transición.
 */
export function skinFrictionSmooth(re: number): number {
  const laminar = 1.328 / Math.sqrt(re);
  if (re < RE_CRITICAL) return laminar;
  return Math.max(laminar, 0.455 / Math.pow(Math.log10(re), 2.58) - 1700 / re);
}

/** Coeficiente de fricción de placa rugosa completamente turbulenta, con altura de rugosidad `k`. */
export function skinFrictionRough(length: number, roughness: number): number {
  return Math.pow(1.89 + 1.62 * Math.log10(length / roughness), -2.5);
}

/** Coeficiente de fricción de una superficie de longitud `length` a Reynolds `re` y Mach `mach`. */
export function skinFriction(re: number, mach: number, roughness: number, length: number): number {
  let cf = skinFrictionSmooth(re);
  if (roughness > 0 && length > roughness) cf = Math.max(cf, skinFrictionRough(length, roughness));
  return cf / Math.pow(1 + 0.144 * mach * mach, 0.65);
}

/** Coeficiente de presión de estancamiento. */
export function stagnationPressure(mach: number): number {
  if (mach <= 1) return 1 + (mach * mach) / 4 + mach ** 4 / 40;
  const m2 = mach * mach;
  return 1.84 - 0.76 / m2 + 0.166 / (m2 * m2) + 0.035 / (m2 * m2 * m2);
}

/** Resistencia de base según el Mach. */
export const baseDragCoefficient = (mach: number): number => (mach < 1 ? 0.12 + 0.13 * mach * mach : 0.25 / mach);

/** Peso de mezcla transónica: 0 hasta M = 0,8 y 1 desde M = 1,2 (suavizado). */
export function transonicWeight(mach: number): number {
  const x = Math.min(Math.max((mach - 0.8) / 0.4, 0), 1);
  return 3 * x * x - 2 * x * x * x;
}

const wettedCache = new WeakMap<object, number>();

/** Área lateral (mojada) del componente de la pila (m²). El resultado se memoiza por componente. */
export function wettedArea(c: StackComponent): number {
  let a = wettedCache.get(c);
  if (a === undefined) {
    a = computeWettedArea(c);
    wettedCache.set(c, a);
  }
  return a;
}

function computeWettedArea(c: StackComponent, slices = 400): number {
  switch (c.type) {
    case 'body-tube':
      return Math.PI * c.outerDiameter * c.length;
    case 'transition': {
      const r1 = c.foreDiameter / 2;
      const r2 = c.aftDiameter / 2;
      return Math.PI * (r1 + r2) * Math.hypot(c.length, r2 - r1);
    }
    case 'nose-cone': {
      const dx = c.length / slices;
      let area = 0;
      for (let i = 0; i < slices; i++) {
        const r1 = noseRadius(c, i * dx);
        const r2 = noseRadius(c, (i + 1) * dx);
        area += Math.PI * (r1 + r2) * Math.hypot(dx, r2 - r1);
      }
      return area;
    }
  }
}

const foreRadius = (c: StackComponent): number => (c.type === 'nose-cone' ? 0 : c.type === 'body-tube' ? c.outerDiameter / 2 : c.foreDiameter / 2);
const aftRadius = (c: StackComponent): number => (c.type === 'nose-cone' ? c.baseDiameter / 2 : c.type === 'body-tube' ? c.outerDiameter / 2 : c.aftDiameter / 2);

export interface DragResult {
  /** Coeficiente de arrastre axial total, referido a A_ref. */
  readonly cd: number;
  readonly friction: number;
  readonly pressure: number;
  readonly base: number;
  readonly warnings: readonly string[];
}

export function dragCoefficient(rocket: Rocket, conditions: FlightConditions): DragResult {
  const mach = machOf(conditions);
  const v = Math.max(conditions.airspeed, MIN_AIRSPEED);
  const nu = conditions.air.kinematicViscosity;
  const d = referenceDiameter(rocket);
  const refArea = (Math.PI * d * d) / 4;
  const length = totalLength(rocket);
  const k = conditions.roughness;
  const warnings: string[] = [];

  // Fricción del cuerpo.
  const wetted = rocket.stack.reduce((s, c) => s + wettedArea(c), 0);
  const bodyForm = 1 + 1.5 * (d / length) ** 1.5 + 7 * (d / length) ** 3;
  const cfBody = skinFriction((v * length) / nu, mach, k, length);
  let friction = (cfBody * bodyForm * wetted) / refArea;

  // Fricción de las aletas y borde de ataque.
  const weight = transonicWeight(mach);
  const cp0 = stagnationPressure(mach);
  let pressure = 0;
  const layout = layoutStack(rocket);
  for (const entry of layout) {
    if (entry.component.type === 'nose-cone') continue;
    for (const f of entry.component.children) {
      if (f.type !== 'fin-set') continue;
      const planform = ((f.rootChord + f.tipChord) * f.span) / 2;
      const sum = f.rootChord + f.tipChord;
      const mac = ((2 / 3) * (f.rootChord ** 2 + f.rootChord * f.tipChord + f.tipChord ** 2)) / sum;
      const cfFin = skinFriction((v * mac) / nu, mach, k, mac);
      friction += (cfFin * (1 + (2 * f.thickness) / mac) * 2 * f.finCount * planform) / refArea;
      const cos2 = f.span ** 2 / (f.span ** 2 + f.sweep ** 2);
      // Borde de ataque plano (sección cuadrada): presión de estancamiento plena a cualquier Mach.
      // Redondeado o con perfil: solo aparece en el régimen transónico (mezcla `weight`).
      const leWeight = f.crossSection === 'square' ? 1 : weight;
      pressure += (leWeight * cp0 * f.finCount * f.thickness * f.span * cos2) / refArea;
    }
  }

  // Escalones de diámetro hacia delante entre componentes de la pila.
  for (let i = 1; i < layout.length; i++) {
    const ra = aftRadius(layout[i - 1]!.component);
    const rf = foreRadius(layout[i]!.component);
    if (rf > ra + 1e-9) pressure += (weight * cp0 * Math.PI * (rf * rf - ra * ra)) / refArea;
  }

  // Base.
  const last = layout[layout.length - 1]!.component;
  const base = (baseDragCoefficient(mach) * Math.PI * aftRadius(last) ** 2) / refArea;

  if (mach > 1) warnings.push('Vuelo supersónico: la resistencia de onda de la ojiva y las transiciones no está modelada');
  return { cd: friction + pressure + base, friction, pressure, base, warnings };
}
